import { useState, type FormEvent } from 'react';
import { api } from '../../services/api';
import { useWordStore } from '../../stores/wordStore';
import { useUiStore } from '../../stores/uiStore';
import './WordEntryForm.css';

interface EntryField {
  name: string;
  label: string;
  required?: boolean;
  type: string;
  options?: string[];
  placeholder?: string;
}

const ENTRY_FIELDS: EntryField[] = [
  { name: 'word', label: 'Word', required: true, type: 'text' },
  { name: 'part_of_speech', label: 'Part of Speech', type: 'select', options: ['noun', 'verb', 'adjective', 'adverb', 'other'] },
  { name: 'phonetics', label: 'Phonetics', type: 'text', placeholder: '/fəˈnɛtɪks/' },
  { name: 'pronunciation_tip', label: 'Pronunciation Tip', type: 'text' },
  { name: 'correct_definition', label: 'Correct Definition', type: 'textarea' },
  { name: 'dictionary_source', label: 'Dictionary Source', type: 'text' },
  { name: 'difficulty', label: 'Difficulty', type: 'select', options: ['Easy', 'Medium', 'Hard'] },
  { name: 'word_themes', label: 'Themes (comma-separated)', type: 'text', placeholder: 'food, culture, science' },
  { name: 'word_vibe', label: 'Vibe', type: 'text', placeholder: 'playful, scholarly, obscure' },
  { name: 'discovery_notes', label: 'Discovery Notes', type: 'textarea', placeholder: 'Where you found this word, why it caught your eye' },
];

export default function WordEntryForm() {
  const { fetchWords, selectWord } = useWordStore();
  const setNewWordMode = useUiStore((s) => s.setNewWordMode);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const updateField = (name: string, value: string): void => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formData.word?.trim()) return;

    setSaving(true);
    setError(null);

    // Trim every field before posting (L25 fix): stray leading/trailing
    // whitespace from paste used to persist into Airtable. Drop fields that are
    // empty after trimming so we don't write blank strings.
    const trimmedFields: Record<string, string> = {};
    for (const [k, v] of Object.entries(formData)) {
      const t = typeof v === 'string' ? v.trim() : v;
      if (t !== '') trimmedFields[k] = t;
    }

    try {
      const result = await api.post('/api/words', { fields: trimmedFields }) as { word: { id: string } };
      await fetchWords();
      setNewWordMode(false);
      await selectWord(result.word.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = (): void => {
    setFormData({});
    setError(null);
  };

  return (
    <div className="word-entry">
      <div className="entry-header">
        <h2 className="entry-title">Add New Word</h2>
        <p className="entry-desc">
          Enter the word. Everything else is optional — Stage 1 will generate the rest.
        </p>
      </div>

      {error && <div className="entry-error">{error}</div>}

      <form onSubmit={handleSubmit} className="entry-form">
        {ENTRY_FIELDS.map((field) => (
          <div key={field.name} className={`entry-field ${field.name === 'word' ? 'entry-field-word' : ''}`}>
            <label className="entry-label">
              {field.label}
              {field.required && <span className="entry-required">*</span>}
            </label>

            {field.type === 'select' ? (
              <select
                className="entry-input"
                value={formData[field.name] || ''}
                onChange={(e) => updateField(field.name, e.target.value)}
              >
                <option value="">—</option>
                {(field.options || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : field.type === 'textarea' ? (
              <textarea
                className="entry-input entry-textarea"
                value={formData[field.name] || ''}
                onChange={(e) => updateField(field.name, e.target.value)}
                placeholder={field.placeholder}
                rows={3}
              />
            ) : (
              <input
                className="entry-input"
                type="text"
                value={formData[field.name] || ''}
                onChange={(e) => updateField(field.name, e.target.value)}
                placeholder={field.placeholder}
                autoFocus={field.name === 'word'}
              />
            )}
          </div>
        ))}

        <div className="entry-actions">
          <button type="button" className="entry-clear" onClick={handleClear} disabled={saving}>
            Clear
          </button>
          <button type="submit" className="entry-submit" disabled={saving || !formData.word?.trim()}>
            {saving ? 'Creating...' : 'Create Word'}
          </button>
        </div>
      </form>
    </div>
  );
}
